import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Td,
  Text,
  Tr,
  useDisclosure,
  useToast
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon, EditIcon } from "@chakra-ui/icons";
import { FaFileImport, FaUsers } from "react-icons/fa";
import { listPoolMembers, deletePoolMember } from "../../../api/partnersApi";
import { useBusinessUnit } from "../../../context/BusinessUnitContext";
import {
  PARTNERS_POOL_STATUS_COLORS,
  PARTNERS_POOL_STATUS_LABELS,
  PARTNERS_POOL_STATUS_LABEL_KEYS,
  PARTNERS_POOL_MEMBER_STATUS_COLORS,
  PARTNERS_POOL_MEMBER_STATUS_LABELS,
  PARTNERS_POOL_MEMBER_STATUS_LABEL_KEYS,
  getStatusLabel
} from "../../../constants/statuses";
import {
  DataTable,
  StatusBadge,
  MiniIconButton,
  EmptyState,
  TablePagination,
} from "../../../components/Portal/ui";
import {
  MODAL_CONTENT_SX,
  MODAL_HEADER_SX,
  MODAL_FOOTER_SX,
} from "../../../components/Portal/portalModalChrome";
import PoolMemberFormModal from "./PoolMemberFormModal";
import ImportPoolMembersFromFileModal from "./ImportPoolMembersFromFileModal";
import ConfirmDialog from "../../../components/Portal/ConfirmDialog";
import { useTranslation } from "react-i18next";



const PoolDetailModal = ({ isOpen, onClose, pool }) => {
  const toast = useToast();
  const { t } = useTranslation("portal");
  const { selectedBusinessUnitId, isLoaded: isBUContextLoaded } = useBusinessUnit();

  const [tabIndex, setTabIndex] = useState(0);

  // Members
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const createMemberModal = useDisclosure();
  const editMemberModal = useDisclosure();
  const importMembersModal = useDisclosure();
  const deleteMemberDialog = useDisclosure();
  const [deleteMemberId, setDeleteMemberId] = useState(null);
  const [editingMember, setEditingMember] = useState(null);

  const poolId = pool?.id;

  const fetchMembers = useCallback(async () => {
    if (!poolId) return;
    setLoadingMembers(true);
    try {
      const res = await listPoolMembers(poolId, { page, pageSize }, {
        buId: selectedBusinessUnitId || undefined
      });
      setMembers(res.items || []);
      setTotalItems(res.totalItems || 0);
      setTotalPages(
        res.totalPages || Math.max(1, Math.ceil((res.totalItems || 0) / pageSize))
      );
    } catch (e) {
      toast({
        title: t("partners.pools.detail.toasts.loadMembersFailed", "Failed to load members"),
        status: "error"
      });
      setMembers([]);
      setTotalItems(0);
      setTotalPages(1);
    } finally {
      setLoadingMembers(false);
    }
  }, [poolId, page, pageSize, selectedBusinessUnitId, toast, t]);

  useEffect(() => {
    if (!isOpen) return;
    if (!isBUContextLoaded) return;
    fetchMembers();
  }, [fetchMembers, isBUContextLoaded, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setPage(1);
    setTabIndex(0);
  }, [isOpen, poolId]);

  const handleMemberDelete = async (memberId) => {
    try {
      await deletePoolMember(poolId, memberId, {
        buId: selectedBusinessUnitId || undefined
      });
      toast({
        title: t("partners.pools.detail.toasts.memberDeleted", "Member removed"),
        status: "success"
      });
      fetchMembers();
    } catch (e) {
      toast({
        title: t("partners.pools.detail.toasts.deleteFailedTitle", "Delete failed"),
        description: e?.response?.data?.message || t("partners.pools.detail.toasts.deleteFailedDescription", "Could not remove member."),
        status: "error"
      });
    }
  };

  const handleMemberSuccess = () => {
    fetchMembers();
  };



  const memberColumns = [
    { key: "name", label: t("partners.pools.detail.members.table.name", "Name"), width: "30%" },
    { key: "email", label: t("partners.pools.detail.members.table.email", "Email") },
    { key: "status", label: t("partners.pools.detail.members.table.status", "Status"), width: "16%" },
    { key: "actions", label: t("partners.pools.detail.members.table.actions", "Actions"), width: "12%", align: "right" },
  ];

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="6xl">
        <ModalOverlay bg="rgba(11,31,58,.45)" />
        <ModalContent sx={MODAL_CONTENT_SX}>
          <ModalHeader sx={MODAL_HEADER_SX}>
            {pool?.name || t("partners.pools.detail.titleFallback", "Pool Details")}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Tabs
              variant="enclosed"
              colorScheme="blue"
              isFitted
              index={tabIndex}
              onChange={setTabIndex}
            >
              <TabList>
                <Tab>{t("partners.pools.detail.tabs.info", "Info")}</Tab>
                <Tab>{t("partners.pools.detail.tabs.members", "Members")}</Tab>
              </TabList>

              <TabPanels>
                {/* Tab 1: Info */}
                <TabPanel px={0} pt={4}>
                  <Box
                    bg="white"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="portal.line"
                    p={3}
                  >
                    {!pool ? (
                      <Text color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                        {t("partners.pools.detail.info.notFound", "Pool not found")}
                      </Text>
                    ) : (
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                        <Box>
                          <Text
                            fontSize="xs"
                            textTransform="uppercase"
                            letterSpacing="wider"
                            color="portal.muted"
                            fontWeight={800}
                            mb={1}
                            sx={{ marginBottom: "4px !important" }}
                          >
                            {t("partners.pools.detail.info.name", "Name")}
                          </Text>
                          <Text fontSize="sm" color="portal.text" sx={{ marginBottom: "0 !important" }}>
                            {pool.name || "-"}
                          </Text>
                        </Box>
                        <Box>
                          <Text
                            fontSize="xs"
                            textTransform="uppercase"
                            letterSpacing="wider"
                            color="portal.muted"
                            fontWeight={800}
                            mb={1}
                            sx={{ marginBottom: "4px !important" }}
                          >
                            {t("partners.pools.detail.info.code", "Code")}
                          </Text>
                          <Text
                            fontSize="sm"
                            color="portal.primary"
                            fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
                            fontWeight={800}
                            sx={{ marginBottom: "0 !important" }}
                          >
                            {pool.code || "-"}
                          </Text>
                        </Box>
                        <Box>
                          <Text
                            fontSize="xs"
                            textTransform="uppercase"
                            letterSpacing="wider"
                            color="portal.muted"
                            fontWeight={800}
                            mb={1}
                            sx={{ marginBottom: "4px !important" }}
                          >
                            {t("partners.pools.detail.info.status", "Status")}
                          </Text>
                          {pool.status ? (
                            <StatusBadge
                              label={getStatusLabel(
                                t,
                                PARTNERS_POOL_STATUS_LABEL_KEYS,
                                PARTNERS_POOL_STATUS_LABELS,
                                pool.status
                              ) || "-"}
                              colorScheme={PARTNERS_POOL_STATUS_COLORS[pool.status] || "gray"}
                            />
                          ) : (
                            <Text fontSize="sm" color="portal.text" sx={{ marginBottom: "0 !important" }}>-</Text>
                          )}
                        </Box>
                        <Box>
                          <Text
                            fontSize="xs"
                            textTransform="uppercase"
                            letterSpacing="wider"
                            color="portal.muted"
                            fontWeight={800}
                            mb={1}
                            sx={{ marginBottom: "4px !important" }}
                          >
                            {t("partners.pools.detail.info.description", "Description")}
                          </Text>
                          <Text fontSize="sm" color="portal.text" sx={{ marginBottom: "0 !important" }}>
                            {pool.description || "-"}
                          </Text>
                        </Box>
                        <Box>
                          <Text
                            fontSize="xs"
                            textTransform="uppercase"
                            letterSpacing="wider"
                            color="portal.muted"
                            fontWeight={800}
                            mb={1}
                            sx={{ marginBottom: "4px !important" }}
                          >
                            {t("partners.pools.detail.info.created", "Created")}
                          </Text>
                          <Text fontSize="sm" color="portal.text" sx={{ marginBottom: "0 !important" }}>
                            {pool.created_at
                              ? new Date(pool.created_at).toLocaleString()
                              : "-"}
                          </Text>
                        </Box>
                        <Box>
                          <Text
                            fontSize="xs"
                            textTransform="uppercase"
                            letterSpacing="wider"
                            color="portal.muted"
                            fontWeight={800}
                            mb={1}
                            sx={{ marginBottom: "4px !important" }}
                          >
                            {t("partners.pools.detail.info.membersCount", "Members")}
                          </Text>
                          <Text fontSize="sm" color="portal.text" sx={{ marginBottom: "0 !important" }}>
                            {pool.members_count ?? pool.membersCount ?? "-"}
                          </Text>
                        </Box>
                      </SimpleGrid>
                    )}
                  </Box>
                </TabPanel>

                {/* Tab 2: Members */}
                <TabPanel px={0} pt={4}>
                  <Flex align="center" justify="space-between" mb={2}>
                    <Text
                      fontSize="sm"
                      fontWeight={800}
                      color="portal.text"
                      sx={{ marginBottom: "0 !important" }}
                    >
                      {loadingMembers
                        ? t("partners.pools.detail.members.summary.loading", "Loading...")
                        : t("partners.pools.detail.members.summary.total", { count: totalItems, defaultValue: "{{count}} member(s)" })}
                    </Text>
                    <HStack spacing={2}>
                      <Button
                        leftIcon={<FaFileImport />}
                        variant="secondary"
                        size="sm"
                        onClick={importMembersModal.onOpen}
                        isDisabled={!poolId}
                      >
                        {t("partners.pools.detail.members.importButton", "Import from file")}
                      </Button>
                      <Button
                        leftIcon={<AddIcon />}
                        variant="primary"
                        size="sm"
                        onClick={createMemberModal.onOpen}
                        isDisabled={!poolId}
                      >
                        {t("partners.pools.detail.members.addButton", "Add Member")}
                      </Button>
                    </HStack>
                  </Flex>

                  <Box
                    bg="white"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="portal.line"
                    overflow="hidden"
                  >
                    <DataTable
                      columns={memberColumns}
                      rows={members}
                      loading={loadingMembers}
                      emptyState={
                        <EmptyState
                          icon={<FaUsers />}
                          title={t("partners.pools.detail.members.noMembers", "No members yet")}
                          hint={t("partners.pools.detail.members.noMembersHint", "Add or import members to populate this pool.")}
                        />
                      }
                      renderRow={(member) => (
                        <Tr key={member.id}>
                          <Td fontWeight="700">
                            {(member.first_name || member.firstName || "")} {(member.last_name || member.lastName || "")}
                          </Td>
                          <Td>{member.email || "-"}</Td>
                          <Td>
                            {member.status ? (
                              <StatusBadge
                                label={getStatusLabel(
                                  t,
                                  PARTNERS_POOL_MEMBER_STATUS_LABEL_KEYS,
                                  PARTNERS_POOL_MEMBER_STATUS_LABELS,
                                  member.status
                                )}
                                colorScheme={PARTNERS_POOL_MEMBER_STATUS_COLORS[member.status] || "gray"}
                              />
                            ) : (
                              "-"
                            )}
                          </Td>
                          <Td>
                            <HStack justify="flex-end" spacing="6px">
                              <MiniIconButton
                                aria-label={t("partners.pools.detail.editMemberAria", "Edit")}
                                icon={<EditIcon />}
                                onClick={() => {
                                  setEditingMember(member);
                                  editMemberModal.onOpen();
                                }}
                              />
                              <MiniIconButton
                                aria-label={t("partners.pools.detail.deleteMemberAria", "Delete")}
                                icon={<DeleteIcon />}
                                color="portal.danger"
                                _hover={{ bg: "danger.50", borderColor: "portal.danger", color: "portal.danger" }}
                                onClick={() => { setDeleteMemberId(member.id); deleteMemberDialog.onOpen(); }}
                              />
                            </HStack>
                          </Td>
                        </Tr>
                      )}
                    />

                    {/* Pagination */}
                    <TablePagination
                      page={page}
                      pageSize={pageSize}
                      totalItems={totalItems}
                      totalPages={totalPages}
                      onPageChange={setPage}
                      onPageSizeChange={(sz) => { setPageSize(sz); setPage(1); }}
                    />
                  </Box>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </ModalBody>
          <ModalFooter sx={MODAL_FOOTER_SX}>
            <Button variant="secondary" size="sm" onClick={onClose}>
              {t("partners.pools.detail.close", "Close")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {poolId && (
        <>
          <PoolMemberFormModal
            isOpen={createMemberModal.isOpen}
            onClose={createMemberModal.onClose}
            initialData={null}
            poolId={poolId}
            onSuccess={handleMemberSuccess}
          />
          <PoolMemberFormModal
            isOpen={editMemberModal.isOpen}
            onClose={() => {
              setEditingMember(null);
              editMemberModal.onClose();
            }}
            initialData={editingMember}
            poolId={poolId}
            onSuccess={handleMemberSuccess}
          />
          <ImportPoolMembersFromFileModal
            isOpen={importMembersModal.isOpen}
            onClose={importMembersModal.onClose}
            poolId={poolId}
            onSuccess={handleMemberSuccess}
          />
        </>
      )}
      <ConfirmDialog
        isOpen={deleteMemberDialog.isOpen}
        onClose={() => { setDeleteMemberId(null); deleteMemberDialog.onClose(); }}
        onConfirm={() => handleMemberDelete(deleteMemberId)}
        title={t("partners.pools.detail.confirmDeleteMemberTitle", { defaultValue: "Remove member?" })}
        body={t("partners.pools.detail.confirmDeleteMember", "Are you sure you want to remove this member?")}
        confirmLabel={t("common.delete", { defaultValue: "Remove" })}
      />
    </>
  );
};

export default PoolDetailModal;
